// Where the shared video plays (src/media.ts): StageHost mounts the one element into a box; PlaybackSurface adds
// the hand-off's states around it (up next, starting, the tap-to-play fallback, ended); PlayerOverlay shows it full
// screen anywhere the editing suite isn't open for that project.
import { useEffect, useRef } from "react";
import { stageVideo } from "../media";
import { closePlayer, tapToPlay } from "../player";
import { isActive, type PlayState } from "../playback";
import { useStore } from "../store";
import { useStageCovers } from "../stage/surface";
import { Icon } from "./bits";

export function StageHost({ poster, controls = true }: { poster?: string; controls?: boolean }) {
  const box = useRef<HTMLDivElement>(null);
  // mount the element once per host: moving a playing <video> in the DOM pauses it
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const v = stageVideo();
    el.appendChild(v);
    return () => {
      if (v.parentElement === el) el.removeChild(v);
      v.controls = false;
      v.removeAttribute("poster");
    };
  }, []);
  useEffect(() => {
    const v = stageVideo();
    v.controls = controls;
    if (poster) v.poster = poster;
    else v.removeAttribute("poster");
  }, [poster, controls]);
  return <div ref={box} className="stage-host" />;
}

/** The hand-off's states over the shared video. One box, no layout change between states (no flashing). */
export function PlaybackSurface({ pb, poster }: { pb: PlayState; poster?: string }) {
  const title = pb.req?.title ?? (pb.req?.what === "render" ? "Your video" : "The clip");
  return (
    <div className={`pb pb-${pb.phase}`}>
      <StageHost poster={poster} controls={pb.phase === "playing" || pb.phase === "ended" || pb.phase === "starting"} />
      {pb.phase === "cued" && (
        <div className="pb-veil">
          <p className="pb-kicker">Up next</p>
          <p className="pb-title">{title}</p>
          <button type="button" className="pb-now" onClick={tapToPlay}>
            <Icon name="play" size={16} /> Play now
          </button>
        </div>
      )}
      {pb.phase === "blocked" && (
        <button type="button" className="pb-tap" onClick={tapToPlay} aria-label={`Tap to watch ${title}`}>
          <span className="pb-tap-ring">
            <Icon name="play" size={40} />
          </span>
          <span className="pb-tap-word">Tap to watch</span>
        </button>
      )}
      {pb.phase === "ended" && (
        <div className="pb-veil pb-veil-end">
          <button type="button" className="pb-now" onClick={tapToPlay}>
            <Icon name="redo" size={16} /> Watch again
          </button>
        </div>
      )}
    </div>
  );
}

/** Full screen, for a `play` anywhere the suite isn't showing that project (and not the library's own player). */
export function PlayerOverlay() {
  const pb = useStore((s) => s.playback);
  const suite = useStore((s) => s.suiteProject);
  // v4: while the Director's stage is up it plays the video itself (src/stage/StageView.tsx)
  const staged = useStageCovers();
  const show = isActive(pb) && pb.req?.surface !== "library" && !(suite && pb.req?.projectId === suite) && !staged;
  useEffect(() => {
    if (!show) return;
    const key = (e: KeyboardEvent) => e.key === "Escape" && closePlayer();
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [show]);
  if (!show || !pb.req) return null;
  return (
    <div className="player-overlay" role="dialog" aria-modal="true" aria-label={pb.req.title ?? "Video"} onClick={closePlayer}>
      <div className="player-box" onClick={(e) => e.stopPropagation()}>
        <PlaybackSurface pb={pb} />
        <div className="player-bar">
          <p>
            {pb.req.title ?? (pb.req.what === "render" ? "Your video" : "Clip")}
            {pb.req.version && !pb.req.title?.includes(`v${pb.req.version}`) ? <span className="ver-tag">v{pb.req.version}</span> : null}
          </p>
          <a className="btn btn-quiet btn-small" href={pb.req.url} download>
            <Icon name="download" size={16} /> Download
          </a>
          <button data-action="player.close" type="button" className="icon-btn" onClick={closePlayer} aria-label="Close video">
            <Icon name="close" />
          </button>
        </div>
      </div>
    </div>
  );
}
